import PropTypes from 'prop-types';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from '../redux/store';
import { asErrorPayload, buildFailure, errorToMessage, isPinnableFailure } from '../utils/popupFailure';

// Remembers the most recent failure inside an action popup so the popup can
// offer "Pin lead with this error". Two sources: global toast errors
// (state.user.error) while the popup is open, and explicit report() calls.

const NOOP_VALUE = { latestFailure: null, report: () => {}, dismiss: () => {} };
const PopupFailureContext = createContext(NOOP_VALUE);

export function PopupFailureProvider({ popupName, open, children }) {
    const [latestFailure, setLatestFailure] = useState(null);
    const error = useSelector((state) => state.user?.error);
    // Compared by identity: two consecutive errors with identical text are still
    // two errors. Updated even while closed so a stale error already in the
    // store is not captured the moment the popup opens.
    const lastSeen = useRef(error);

    useEffect(() => {
        if (error === lastSeen.current) return;
        lastSeen.current = error;
        if (!open || !isPinnableFailure(error)) return;
        setLatestFailure(buildFailure({ message: errorToMessage(error), popup: popupName, status: error.status }));
    }, [error, open, popupName]);

    useEffect(() => {
        if (!open) setLatestFailure(null);
    }, [open]);

    const report = useCallback((message, step) => {
        if (!open) return;
        // Same non-failure filter as toast errors (auth messages, empty text, …).
        if (!isPinnableFailure(message)) return;
        setLatestFailure(buildFailure({ message: errorToMessage(message), popup: popupName, step, status: asErrorPayload(message).status }));
    }, [open, popupName]);
    const dismiss = useCallback(() => setLatestFailure(null), []);

    const value = useMemo(() => ({ latestFailure, report, dismiss }), [latestFailure, report, dismiss]);
    return <PopupFailureContext.Provider value={value}>{children}</PopupFailureContext.Provider>;
}

PopupFailureProvider.propTypes = {
    popupName: PropTypes.string.isRequired,
    open: PropTypes.bool,
    children: PropTypes.node,
};

export const usePopupFailure = () => useContext(PopupFailureContext);

// Report `value` (an error message/object, null when clear) to the surrounding
// popup — but only errors that APPEAR after this component mounted. For errors
// held outside the component (e.g. Redux) that outlive the popup, a remount
// would otherwise re-report a stale failure on the next open.
export function useReportNewFailure(value, step) {
    const { report } = usePopupFailure();
    const seen = useRef(value);
    useEffect(() => {
        if (value && value !== seen.current) report(value, step);
        seen.current = value;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);
}
